/* eslint-disable react/prop-types */
/* eslint-disable no-console */
/* eslint-disable max-len */
import React, { useContext, useState, useEffect, useRef } from 'react';
import { Box, Checkbox, Chip, FormControlLabel, Grid, InputAdornment, Paper, Stack, TextField, Typography, CircularProgress, Tooltip } from '@mui/material';
import { LanguageContext } from '../../../../../../../libs/Context';
import SearchIcon from '@mui/icons-material/Search';
import { useSelector } from 'react-redux';
import { getRelations, getDepartments, getUsers } from '../../../../../../../containers/actions/organization_structure';
import { getConfigDetails } from '../../../../../../actions/meeting_actions';
import useInterSectionObserver from '../../../../../../../libs/CustomHooks/useIntersectionObserver';
import useDebounce from '../../../../../../../components/Hooks/useDebounce';
import { useScroll } from '../../../../../../../libs/CustomHooks/useScroll';
import useDidMountEffect from '../../../../../../../libs/CustomHooks/UseDidMountEffect';

interface User {
name: string;
email: string;
role: string;
userId?: string;
}

interface Relation {
_id: string;
name: string;
description?: string;
}

interface Department {
_id: string;
name: string;
description?: string;
}

interface Category {
label: string;
_id?: string;
state: boolean;
count?: number;
type?: string; // 'internal' for employees, 'relation' for business relations
}

interface SelectSearchParticipantsProps {
onParticipantsSelected?: (participants: User[]) => void;
onDepartmentsSelected?: (category: string, selectedChips: Record<string, string[]>) => void;
onRelationsSelected?: (relations: string[]) => void;
initialInternalEmployees?: {
allSelected?: boolean;
departments?: Array<{name: string; count?: number}>;
specificUsers?: Array<{userId: string; name: string; email: string; role?: string}>;
};
initialUserGroups?: Array<{type: string; count?: number}>;
isEdit?: boolean;
}

const SelectSearchParticipants: React.FC<SelectSearchParticipantsProps> = ({
	onParticipantsSelected,
	onDepartmentsSelected,
	onRelationsSelected,
	initialInternalEmployees,
	initialUserGroups,
	isEdit = false
}) =>
{
	// Initialize based on whether there are existing selections
	const hasExistingSelections = Boolean(
		(initialUserGroups ?? []).length > 0 ||
		(initialInternalEmployees?.specificUsers ?? []).length > 0 ||
		(initialInternalEmployees?.departments ?? []).length > 0
	);

	// For new category, select Internal Employees and All
	// For editing, check allSelected value
	const isNewCategory = !isEdit;
	const shouldSelectInternalEmployees = isNewCategory || Boolean(initialInternalEmployees?.allSelected);

	const [ selectedChips, setSelectedChips ] = useState<Record<string, string[]>>(
		shouldSelectInternalEmployees ? { 'Internal Employees': [ 'All' ] } : {}
	);

	const [ checkedState, setCheckedState ] = useState<Record<string, boolean>>({
		'Internal Employees' : shouldSelectInternalEmployees
	});

	const { language } = useContext(LanguageContext);
	const [ selectedUsers, setSelectedUsers ] = useState<User[]>([]);
	const [ isLoading, setIsLoading ] = useState<boolean>(false);
	const [ searchLoading, setSearchLoading ] = useState<boolean>(false);
	const [ allUsers, setAllUsers ] = useState<any>([]);
	const [ filteredUsers, setFilteredUsers ] = useState<any>([]);
	const userData = useSelector((state: any) => state.mysession.session);
	const [ page, setPage ] = useState(0);
	const [ searchVal, setSearchVal ] = useState<string>('');
	const [ totalCount, setTotalCount ] = useState<number>(0);
	const loaderRef = useRef<boolean>(false);
	const lastEleRef = useRef<HTMLDivElement | null>(null);
	const itemsPerpage = 12;

	const debouncedSearchVal = useDebounce(searchVal, 500);

	// Initial state for the Internal Employees category
	const internalEmployeesCategory: Category = {
		label : 'Internal Employees',
		state : true,
		type  : 'internal'
	};

	// Initialize categories with the Internal Employees plus relations from API
	const [ categories, setCategories ] = useState<Category[]>([
		internalEmployeesCategory
	]);

	// Initialize departments list for when Internal Employees is checked
	const [ chipsData, setChipsData ] = useState<Array<{label: string, _id?: string, count?: number}>>([
		{ label: 'All' }
	]);

	// Effect to notify parent of initial All selection for new category
	useEffect(() =>
	{
		if (isNewCategory && onDepartmentsSelected && typeof onDepartmentsSelected === 'function')
		{
			onDepartmentsSelected('Internal Employees', { 'Internal Employees': [ 'All' ] });
		}
	}, [ isNewCategory, onDepartmentsSelected ]);

	// Helper function to truncate text with ellipsis
	const truncateText = (text: string, maxLength: number = 15): string =>
	{
		return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
	};

	const getCount = async (query:any) =>
	{
		try
		{
			query.count = true;
			const res = await getConfigDetails(query);

			if (res?.status)
			{
				setTotalCount(res?.count || 0);
			}
			else
			{
				setTotalCount(totalCount || 0);
			}
		}
		catch (err)
		{
			console.error('Error fetching host count:', err);
			setTotalCount(0);
		}
	};
	const getUsersData = async () =>
	{
		try
		{
			setSearchLoading(true);
			const query:any = {
				uid     : userData?.userId,
				comid   : userData?.companyId,
				empRole : userData?.empRole,
				role    : userData?.role,
				skip    : page,
				limit   : itemsPerpage,
				status  : 'approve'
			};

			if (searchVal !== '')
			{
				query.search = searchVal;
			}

			const res = await getConfigDetails(query);

			if (res?.status)
			{
				const combinedData = page === 0 ? res?.data : [ ...allUsers, ...res?.data ];

				if (page === 0)
				{
					if (res?.data?.length >= itemsPerpage)
					{
						await getCount(query);
					}
					else
					{
						setTotalCount(res?.data?.length || 0);
					}
				}

				setAllUsers(combinedData);
				setFilteredUsers(combinedData);
				loaderRef.current = false;
			}
		}
		catch (error)
		{
			console.error('Error fetching users:', error);
			loaderRef.current = false;
		}
		finally
		{
			setSearchLoading(false);
		}
	};

	// Fetch relations, departments, and users on component mount
	useEffect(() =>
	{
		const fetchData = async () =>
		{
			if (!userData?.companyId) return;

			setIsLoading(true);
			try
			{
				// Fetch relations
				const relationsData = await getRelations(userData.companyId);

				// Create categories from relations data
				const relationCategories = relationsData.map((relation) => ({
					label : relation.name,
					state : false,
					_id   : relation._id,
					type  : 'relation'
				}));

				// Set categories with Internal Employees first, then relations
				const allCategories = [
					internalEmployeesCategory,
					...relationCategories
				];

				setCategories(allCategories);

				// Initialize checked state from all categories
				const initialCheckedState = allCategories.reduce((acc, category) => ({
					...acc,
					[category.label] : category.state
				}), {});

				// setCheckedState(initialCheckedState);

				// Save the raw relations data

				// Fetch departments
				const departmentsData = await getDepartments(userData.companyId);

				// Create chips data from departments
				const departmentChips = departmentsData.map((dept) => ({
					label : dept.name,
					_id   : dept._id
				}));

				// Set departments with All option first, then departments
				setChipsData([
					{ label: 'All' },
					...departmentChips
				]);

				// Save the raw departments data

				// Fetch users
				// const usersData = await getUsers(userData.companyId);
			}
			catch (error)
			{
				console.error('Error fetching data:', error);
			}
			finally
			{
				setIsLoading(false);
			}
		};

		fetchData();
		// getUsersData();
	}, [ userData?.companyId ]);

	useEffect(() =>
	{
		getUsersData();
	}, [ page ]);

	useDidMountEffect(() =>
	{
		setAllUsers([]);
		page > 0
			? setPage(0)
			: getUsersData();
	}, [ debouncedSearchVal ]);

	// Initialize from props if available
	useEffect(() =>
	{
		if (initialInternalEmployees || initialUserGroups)
		{
			// Set Internal Employees checked state
			if (initialInternalEmployees)
			{
				// Check if allSelected is true
				if (initialInternalEmployees.allSelected)
				{
					setSelectedChips((prev) => ({
						...prev,
						'Internal Employees' : [ 'All' ]
					}));
					setCheckedState((prev) => ({
						...prev,
						'Internal Employees' : true
					}));
				}
				// Set departments if available
				else if (initialInternalEmployees.departments && initialInternalEmployees.departments.length > 0)
				{
					setSelectedChips({
						'Internal Employees' : initialInternalEmployees.departments.map((dept) => dept.name)
					});
					setCheckedState((prev) => ({ ...prev, 'Internal Employees': true }));
				}

				// Set specific users if available
				if (initialInternalEmployees.specificUsers && initialInternalEmployees.specificUsers.length > 0)
				{
					setSelectedUsers(initialInternalEmployees.specificUsers.map((user) => ({
						userId : user.userId,
						name   : user.name,
						email  : user.email,
						role   : user.role || ''
					})));
					setCheckedState((prev) => ({ ...prev, 'Internal Employees': true }));
				}
			}

			// Set user groups (relations) checked state
			if (initialUserGroups && initialUserGroups.length > 0)
			{
				setCheckedState((prevCheckedState) =>
				{
					const newCheckedState = { ...prevCheckedState };

					// For each user group, check the corresponding category
					initialUserGroups.forEach((group) =>
					{
						newCheckedState[group.type] = true;
					});

					return newCheckedState;
				});
			}
		}
	}, [ initialInternalEmployees, initialUserGroups ]);

	// Notify parent when selected chips (departments) change
	// useEffect(() =>
	// {
	// 	if (onDepartmentsSelected && typeof onDepartmentsSelected === 'function')
	// 	{
	// 		onDepartmentsSelected('Internal Employees', selectedChips);
	// 	}
	// }, [ selectedChips, onDepartmentsSelected ]);

	// // Notify parent when selected relations change
	// useEffect(() =>
	// {
	// 	if (onRelationsSelected && typeof onRelationsSelected === 'function')
	// 	{
	// 		// Get the selected relation names from checkedState
	// 		const selectedRelations = Object.entries(checkedState)
	// 			.filter(([ label, isChecked ]) =>
	// 				isChecked && label !== 'Internal Employees')
	// 			.map(([ label ]) => label);

	// 		onRelationsSelected(selectedRelations);
	// 	}
	// }, [ checkedState, onRelationsSelected ]);

	// Filter users based on selected categories and departments
	useEffect(() =>
	{
		// Only reset autocomplete when the user pool actually changes
		// Don't reset on every checkedState change to prevent popup from closing
		const userPool = [ ...allUsers ];

		// If no categories are checked, show no users
		const anyChecked = Object.values(checkedState).some((state) => state);

		if (!anyChecked)
		{
			setFilteredUsers([]);

			return;
		}

		// Filter by selected categories and departments
		// For now, this is a placeholder for actual filtering logic
		// In a real implementation, you'd need to filter users based on:
		// 1. Whether they belong to Internal Employees
		// 2. Whether they belong to selected departments
		// 3. Whether they belong to selected relations

		// For now, we're keeping all users available but showing how the filtering would work
		setFilteredUsers(userPool);
	}, [ checkedState, selectedChips, allUsers ]);

	// Notify parent component when selected users change
	// useEffect(() =>
	// {
	// 	if (onParticipantsSelected && typeof onParticipantsSelected === 'function')
	// 	{
	// 		onParticipantsSelected(selectedUsers);
	// 	}
	// }, [ selectedUsers, onParticipantsSelected ]);

	// Handle change in Autocomplete selection

	// Handle removing a user from selected users
	const handleRemoveUser = (userToRemove: User) =>
	{
		const updatedUsers = selectedUsers.filter((user) =>
			user.userId !== userToRemove.userId || user.email !== userToRemove.email
		);

		setSelectedUsers(updatedUsers);
		if (onParticipantsSelected && typeof onParticipantsSelected === 'function')
		{
			onParticipantsSelected(updatedUsers);
		}
	};

	const handleChipClick = (category: string, chipLabel: string, e: React.MouseEvent) =>
	{
		// Stop event propagation
		e.stopPropagation();

		setSelectedChips((prev) =>
		{
			// If "All" is clicked, handle differently
			if (chipLabel === 'All')
			{
				// If All is already selected, deselect it
				if (prev[category]?.includes('All'))
				{
					const newChips = { ...prev };

					delete newChips[category];
					if (onDepartmentsSelected && typeof onDepartmentsSelected === 'function')
					{
						onDepartmentsSelected('Internal Employees', newChips);
					}

					return newChips;
				}

				// Otherwise, select only All and deselect others
				const newChips = {
					...prev,
					[category] : [ 'All' ]
				};

				if (onDepartmentsSelected && typeof onDepartmentsSelected === 'function')
				{
					onDepartmentsSelected('Internal Employees', newChips);
				}

				return newChips;
			}

			// Handle regular departments
			const currentSelected = prev[category] || [];

			// If "All" is currently selected, remove it when selecting a specific department
			let newSelected = currentSelected.includes('All')
				? []
				: [ ...currentSelected ];

			// Toggle the clicked chip
			if (newSelected.includes(chipLabel))
			{
				newSelected = newSelected.filter((item) => item !== chipLabel);
			}
			else
			{
				newSelected.push(chipLabel);
			}

			// If no chips are selected, return empty object
			if (newSelected.length === 0)
			{
				const newChips = { ...prev };

				delete newChips[category];
				if (onDepartmentsSelected && typeof onDepartmentsSelected === 'function')
				{
					onDepartmentsSelected('Internal Employees', newChips);
				}
				setCheckedState((prevState) => ({ ...prevState, 'Internal Employees': false }));

				return newChips;
			}

			const newChips = {
				...prev,
				[category] : newSelected.length > 0 ? newSelected : [ 'All' ] // Default to All if no specific selection
			};

			if (onDepartmentsSelected && typeof onDepartmentsSelected === 'function')
			{
				onDepartmentsSelected('Internal Employees', newChips);
			}

			return newChips;
		});
	};

	// Separate handler for checkbox onChange event
	const handleCheckboxChange = (categoryLabel: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
	{
		e.stopPropagation();
		setCheckedState((prev) =>
		{
			const newState = { ...prev, [categoryLabel]: !prev[categoryLabel] };

			// When Internal Employees is checked, always select 'All' chip
			if (categoryLabel === 'Internal Employees' && (!prev['Internal Employees'] || selectedChips?.['Internal Employees']?.[0] !== 'All'))
			{
				const newChips = { 'Internal Employees': [ 'All' ] };

				setSelectedChips(newChips);
				if (onDepartmentsSelected && typeof onDepartmentsSelected === 'function')
				{
					onDepartmentsSelected('Internal Employees', newChips);
				}
			}
			// Clear selected chips when a category is unchecked
			else if (!newState[categoryLabel] && selectedChips[categoryLabel])
			{
				setSelectedChips((previous) =>
				{
					const newChips = { ...previous };

					delete newChips[categoryLabel];
					if (onDepartmentsSelected && typeof onDepartmentsSelected === 'function')
					{
						onDepartmentsSelected('Internal Employees', newChips);
					}

					return newChips;
				});
			}

			// Notify parent of relation changes
			if (onRelationsSelected && typeof onRelationsSelected === 'function')
			{
				const selectedRelations = Object.entries(newState)
					.filter(([ label, isChecked ]) => isChecked && label !== 'Internal Employees')
					.map(([ label ]) => label);

				onRelationsSelected(selectedRelations);
			}

			return newState;
		});
	};

	// Check if any departments are currently selected for visual indicator
	const isDepartmentSelected = (category: string, chipLabel: string) =>
	{
		return selectedChips[category]?.includes(chipLabel) || false;
	};

	// Get the currently selected departments as a string for display
	const getSelectedDepartmentsText = () =>
	{
		if (!selectedChips['Internal Employees'] || selectedChips['Internal Employees'].length === 0)
		{
			return '';
		}

		if (selectedChips['Internal Employees'].includes('All'))
		{
			return 'All Departments';
		}

		return selectedChips['Internal Employees'].join(', ');
	};
	// const LoadMoreList = useInterSectionObserver({
	// 	lastEleRef, loaderRef, count : totalCount, data : allUsers, setPage
	// });

	useScroll({
		lastEleRef,
		loaderRef,
		count : totalCount,
		data  : allUsers,
		setPage
	});

	// useEffect(() =>
	// {
	// 	LoadMoreList();
	// }, [ lastEleRef, totalCount, allUsers ]);

	const handleEmployeeListSelect = (option: any, isSelected: boolean) =>
	{
		setSelectedUsers((prevUsers) =>
		{
			const newUsers = isSelected
				? prevUsers.filter(
					(u) => !(u.userId === option.userId || u.email === option.email)
				)
				: [ ...prevUsers, option ];

			if (onParticipantsSelected)
			{
				onParticipantsSelected(newUsers);
			}

			return newUsers;
		});
	};

	// const isPartialEmplyeeSelected = selectedChips['Internal Employees']?.length > 1 && !selectedChips['Internal Employees']?.includes('All');
	const isPartialEmplyeeSelected =
	(selectedChips?.['Internal Employees']?.length > 0 && selectedChips?.['Internal Employees']?.[0] !== 'All') ||
	(!selectedChips['Internal Employees']?.includes('All') && selectedUsers?.length > 0);

	return (
		<Grid container onClick={(e) => e.stopPropagation()}>
			<Grid item xs={12} py={2}>
				<Typography variant='body1' fontWeight={500}>{language?.selectParticiapntAccess || 'Select Participant Access'}</Typography>
			</Grid>
			<Grid item display='flex' flexDirection='row' sx={{ flexWrap: 'wrap' }}>
				{isLoading
					? (
						<Box display='flex' alignItems='center' mt={1}>
							<CircularProgress size={20} />
							<Typography ml={1}>Loading ...</Typography>
						</Box>
					)
					: (
						<>
							<div>
								<FormControlLabel
									control={
										<Checkbox
											checked={(checkedState['Internal Employees'] && selectedChips?.['Internal Employees']?.[0] === 'All') || false}
											indeterminate={isPartialEmplyeeSelected}
											onChange={handleCheckboxChange('Internal Employees')}
										/>
									}
									label={`${'Internal Employees'}`}
								/>
								{(selectedChips?.['Internal Employees']?.length > 0) && (
									<Stack direction='row' alignItems='center' my={1} spacing={1} sx={{ display: 'flex', flexWrap: 'wrap' }}>
										{chipsData.map((chip) => (
											<Chip
												size='small'
												key={chip.label}
												label={`${chip.label}`}
												variant={isDepartmentSelected('Internal Employees', chip.label) ? 'filled' : 'outlined'}
												sx={{
													backgroundColor : isDepartmentSelected('Internal Employees', chip.label) ? '#24A4C4' : 'transparent',
													color           : isDepartmentSelected('Internal Employees', chip.label) ? 'white' : 'black',
													borderColor     : isDepartmentSelected('Internal Employees', chip.label) ? 'transparent' : '#666666',
													'&:hover'       : { backgroundColor: isDepartmentSelected('Internal Employees', chip.label) ? '#1b8ba7' : '#e0e0e0' },
													margin          : '4px !important',
													paddingBottom   : '1px',
													borderRadius    : '10px'
												}}
												onClick={(e) => handleChipClick('Internal Employees', chip.label, e)}
											/>
										))}
									</Stack>
								)}
							</div>
							{categories.map((category) => category.label !== 'Internal Employees' && (
								<div key={category.label}>
									<FormControlLabel
										control={
											<Checkbox
												checked={checkedState[category.label] || false}
												onChange={handleCheckboxChange(category.label)}
											/>
										}
										label={`${category.label}`}
									/>
								</div>
							))}
						</>

					)}
			</Grid>

			{getSelectedDepartmentsText() && (
				<Grid item xs={12} mt={1}>
					<Typography variant='body2' color='textSecondary'>
						<strong>Selected:</strong> {getSelectedDepartmentsText()}
					</Typography>
				</Grid>
			)}

			<Grid item xs={12} mt={2}>
				{/* Display selected users as chips with the same styling as department chips */}
				{selectedUsers.length > 0 && (
					<Stack
						direction='row'
						alignItems='center'
						spacing={1}
						mt={1.5}
						sx={{
							display  : 'flex',
							flexWrap : 'wrap',
							paddingY : 0.5
						}}
						onClick={(e) => e.stopPropagation()}
					>
						{selectedUsers.map((user) => (
							<Tooltip title={`${user.name} (${user.email})`} key={user.userId || user.email}>
								<Chip
									size='small'
									label={truncateText(`${user.name} (${user.email})`)}
									onDelete={(e) =>
									{
										// Stop propagation for delete operation
										if (e)
										{
											e.stopPropagation();
										}
										handleRemoveUser(user);
									}}
									onClick={(e) => e.stopPropagation()} // Stop propagation on chip click
									sx={{
										backgroundColor : '#24A4C4',
										color           : 'white',
										borderRadius    : '10px',
										margin          : '4px !important',
										paddingBottom   : '1px',
										'&:hover'       : {
											backgroundColor : '#1b8ba7'
										},
										'& .MuiChip-deleteIcon' : {
											color     : 'white',
											'&:hover' : {
												color : '#f0f0f0'
											}
										}
									}}
								/>
							</Tooltip>
						))}
					</Stack>
				)}
				{/* Autocomplete with no rendered tags */}
				<div style={{ position: 'relative', width: '100%' }}>
					<TextField
						fullWidth
						placeholder='Search by name or email'
						size='small'
						onClick={(e) => e.stopPropagation()}
						onChange={(e) =>
						{
							setSearchVal(e.target.value.replace(/^\s+/, ''));
						}}
						InputProps={{
							startAdornment : (
								<InputAdornment position='start'>
									<SearchIcon />
								</InputAdornment>
							)
						}}
						sx={{
							'& .MuiOutlinedInput-root' : {
								'& fieldset' : {
									borderColor : 'rgba(0, 0, 0, 0.23)'
								},
								'&:hover fieldset' : {
									borderColor : 'rgba(0, 0, 0, 0.23)'
								},
								'&.Mui-focused fieldset' : {
									borderColor : '#1976d2'
								}
							}
						}}
					/>
					{allUsers.length === 0 && !searchLoading && (
						<Box
							sx={{
								display        : 'flex',
								alignItems     : 'center',
								justifyContent : 'center',
								mt             : 1,
								width          : '100%',
								gap            : 1
							}}
						>
							<Typography ml={1} sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>
								No results found
							</Typography>
						</Box>
					)}
					<Paper
						sx={{
							position  : 'absolute',
							top       : '100%',
							left      : 0,
							right     : 0,
							maxHeight : '300px',
							overflowY : 'auto',
							mt        : 1,
							zIndex    : 1000,
							display   : (allUsers.length > 0 || searchLoading) ? 'block' : 'none'
						}}
					>
						{searchLoading && allUsers.length === 0 && (
							<Box
								sx={{
									display        : 'flex',
									alignItems     : 'center',
									justifyContent : 'center',
									mt             : 1,
									width          : '100%',
									gap            : 1
								}}
							>
								<CircularProgress size={16} />
								<Typography ml={1}>Loading ...</Typography>
							</Box>
						)}
						{allUsers.map((option, index) =>
						{
							const isSelected = selectedUsers.some(
								(u) => u.userId === option.userId || u.email === option.email
							);

							return (
								<Box
									key={option.userId || option.email}
									sx={{
										display    : 'flex',
										alignItems : 'center',
										p          : 1,
										'&:hover'  : {
											backgroundColor : 'rgba(0, 0, 0, 0.04)'
										},
										cursor : 'pointer'
									}}
									onClick={() => handleEmployeeListSelect(option, isSelected)}
								>
									<Checkbox
										checked={isSelected}
									/>
									<Stack
										sx={{ ml: 2, width: '100%', cursor: 'default' }}
										direction={'column'}
									>
										<Typography variant='body2'>{option.name}</Typography>
										<Typography variant='caption' color='#737373'>
											{option.email}
										</Typography>
									</Stack>
									{index === (filteredUsers.length - 2) && (
										<div
											ref={totalCount > allUsers?.length ? lastEleRef : undefined}
											style={{ height: '2px', width: '100%' }}
										/>
									)}
								</Box>
							);
						})}
						{searchLoading && page > 0 && (
							<Box
								sx={{
									display        : 'flex',
									alignItems     : 'center',
									justifyContent : 'center',
									mt             : 1,
									width          : '100%',
									gap            : 1
								}}
							>
								<CircularProgress size={16} />
								<Typography ml={1} sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>
									Loading more ...
								</Typography>
							</Box>
						)}
					</Paper>
				</div>
			</Grid>
		</Grid>
	);
};

export default SelectSearchParticipants;